/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '@/api';
import type { IgnoredCertificate, TrustAnchor } from '@/api/areas/declarations';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { EmptyState, Loading } from '@/components/States';
import { formatDate } from '@/lib/format';
import { useApi } from '@/lib/useApi';

/**
 * Cài đặt › Tên miền & TLD › Chứng chỉ gốc tin cậy: the trust anchors admin-api loaded (read-only;
 * config/trust/vn-ca and the allow-listed runtime directory, docs/operations/declarations.md).
 */
export function TrustAnchorsPage() {
  const { t } = useTranslation('declarations');
  useDocumentTitle(t('trust.title'));
  const result = useApi((signal) => api.getTrustStore({ signal }), []);
  const columns: Column<TrustAnchor>[] = [
    { key: 'subject', header: t('trust.subject'), cell: (a) => <span className="text-sm">{a.subject}</span> },
    { key: 'issuer', header: t('trust.issuer'), cell: (a) => <span className="text-xs">{a.self_signed ? t('trust.selfSigned') : a.issuer}</span> },
    {
      key: 'validity',
      header: t('trust.validity'),
      cell: (a) => (
        <span className="text-xs">
          {a.not_before ? formatDate(a.not_before) : '—'} → {a.not_after ? formatDate(a.not_after) : '—'}{' '}
          {a.valid_now === false ? <Badge tone="crit">{t('trust.expired')}</Badge> : null}
        </span>
      ),
    },
    { key: 'sha', header: t('trust.fingerprint'), cell: (a) => <code className="text-xs break-all">{a.sha256}</code> },
    {
      key: 'file',
      header: t('trust.file'),
      cell: (a) => (
        <span className="grid gap-1 text-xs">
          <code>{a.file}</code>
          <Badge tone={a.source === 'EXTRA' ? 'info' : 'neutral'}>{a.source === 'EXTRA' ? t('trust.sourceExtra') : t('trust.sourceRepo')}</Badge>
        </span>
      ),
    },
  ];
  const ignoredColumns: Column<IgnoredCertificate>[] = [
    { key: 'subject', header: t('trust.subject'), cell: (a) => <span className="text-sm">{a.subject}</span> },
    { key: 'reason', header: t('trust.reason'), cell: (a) => <Badge tone="warn">{t(`trust.ignoredReason.${a.reason}` as 'trust.ignoredReason.NOT_A_CA', { defaultValue: a.reason })}</Badge> },
    { key: 'sha', header: t('trust.fingerprint'), cell: (a) => <code className="text-xs break-all">{a.sha256}</code> },
    { key: 'file', header: t('trust.file'), cell: (a) => <code className="text-xs">{a.file}</code> },
  ];
  return (
    <>
      <PageHeader title={t('trust.title')} subtitle={t('trust.description')} />
      {result.status === 'loading' ? <Loading rows={4} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('trust.loadError')} /> : null}
      {result.status === 'success' ? (
        <div className="grid gap-6">
          <StatGrid>
            <StatCard label={t('trust.count')} value={result.data.count} tone={result.data.count ? 'ok' : 'crit'} icon={ShieldCheck} />
            <StatCard label={t('trust.allowList')} value={result.data.allow_list_count} hint={result.data.allow_list_file ?? t('trust.noExtraDir')} />
          </StatGrid>
          {result.data.count === 0 ? <Callout tone="warn" title={t('trust.emptyTitle')}>{t('trust.emptyBody')}</Callout> : null}
          <Callout tone="info">{t('trust.howTo', { dir: result.data.dir, extra: result.data.extra_dir ?? '—' })}</Callout>
          <Section flush title={t('trust.anchors')}>
            <DataTable columns={columns} rows={result.data.anchors} rowKey={(a) => a.sha256} empty={<EmptyState title={t('trust.emptyTitle')} icon={ShieldCheck} />} />
          </Section>
          {result.data.ignored.length ? (
            <Section flush title={t('trust.ignored')} description={t('trust.ignoredDescription')}>
              <DataTable columns={ignoredColumns} rows={result.data.ignored} rowKey={(a) => `${a.file}:${a.sha256}`} />
            </Section>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
